import clone from "lodash/clone";
import omit from "lodash/omit";
import {z} from "zod";

/**
 * There are exactly three places we run Firebase at the moment
 *  - production and staging are real projects
 *  - emulated represents when we run Firebase against the emulators
 *
 * We always rewrite prod as production
 */
export const FirebaseEnvironment = z.preprocess(
    (value) => {
        if (value === "prod") return "production";
        return value;
    },
    z.enum(["production", "staging", "emulated"]),
);
export type FirebaseEnvironment = z.infer<typeof FirebaseEnvironment>;

/**
 * These are the environment variables that are used to determine the Firebase environment
 *
 * For general use, we try to point Firebase at the same environment the application is pointed
 * - We prefer APPLICATION_ENV for backends (including containerized apps)
 * - We prefer REACT_APP_ENVIRONMENT_NAME for frontends that are built with CRA
 *
 * In some cases, you may need to run an application with one environment, and point its Firebase configuration at another
 * environment. In that case:
 * - We prefer FIREBASE_ENVIRONMENT for backends
 * - We prefer REACT_APP_FIREBASE_ENVIRONMENT for frontends
 * Whatever you specify for these will take precedence over the application environment.
 */
declare global {
    namespace NodeJS {
        interface ProcessEnv {
            FIREBASE_ENVIRONMENT?: string; // used by containerized apps
            REACT_APP_FIREBASE_ENVIRONMENT?: string; // used by frontend

            APPLICATION_ENV?: string; // used by containerized apps, as a fallback from FIREBASE_ENVIRONMENT
            REACT_APP_ENVIRONMENT_NAME?: string; // used by frontend, as a fallback from FIREBASE_ENVIRONMENT
        }
    }
}

/**
 * Safely obtain process.env with DefinePlugin support
 *
 * These variable are actually filled in by the DefinePlugin at bundle time, so they are compile-time values,
 * baked into the build. For this reason, we can't inject the whole of `process.env` as an application service: the
 * define plugin looks specifically for these full property accesses.
 *
 * This makes the accesses of the environment in getConfigFromEnv() work for both bundled apps and Node
 *
 * This function can't be abstracted: the environment variables accesses must appear in the code as the literal
 * "process.env.REACT_APP_ENVIRONMENT_NAME" name for them to be replaced by the bundler
 */
function getProcessEnvSafely(): NodeJS.ProcessEnv {
    return {
        ...((hasProcessAccess() ? (process?.env ?? {}) : {}) as NodeJS.ProcessEnv),
        REACT_APP_ENVIRONMENT_NAME: process.env.REACT_APP_ENVIRONMENT_NAME,
        REACT_APP_FIREBASE_ENVIRONMENT: process.env.REACT_APP_FIREBASE_ENVIRONMENT,
    };
}

/**
 * Gets the Firebase environment that should be used, in the current process/context
 *
 * Works in all places safely. In some cases, such as Cypress, you may want to inject the environment variables as a
 * parameter, and those will take precedence over the real environment.
 *
 * The logger is so that we can report invalid environment names as warnings at runtime, it's optional.
 */
export function getFirebaseEnvironment(overrideProcessEnv: Partial<typeof process.env> = {}): FirebaseEnvironment {
    const env = {...getProcessEnvSafely(), ...overrideProcessEnv};

    const sources = [
        env.FIREBASE_ENVIRONMENT,
        env.APPLICATION_ENV,
        env.REACT_APP_FIREBASE_ENVIRONMENT,
        env.REACT_APP_ENVIRONMENT_NAME,
    ];

    for (const source of sources) {
        const parsed = FirebaseEnvironment.safeParse(source);
        if (parsed.success) {
            return parsed.data;
        }
    }

    return "production";
}

/**
 * These are env vars used to point at the emulator
 */
const emulatorEnvVars = [
    "FIREBASE_EMULATOR_HUB",
    "FIREBASE_FIRESTORE_EMULATOR_ADDRESS",
    "FIRESTORE_EMULATOR_HOST",
    "FIREBASE_AUTH_EMULATOR_HOST",
] as const;

/**
 * host:port for an explicit Firestore emulator connection (firestore.settings({host})).
 *
 * Reads FIREBASE_FIRESTORE_EMULATOR_ADDRESS, NOT the reserved FIRESTORE_EMULATOR_HOST:
 * the latter makes @google-cloud/firestore auto-redirect ALL traffic (including
 * production) to the emulator, bypassing withoutEmulatorEnvVars(). The custom var
 * is set so opt-in connections can find the emulator without that global redirect.
 */
export function getFirestoreEmulatorAddress(): string {
    const address = hasProcessAccess() ? process.env.FIREBASE_FIRESTORE_EMULATOR_ADDRESS : undefined;
    return address ?? "127.0.0.1:8080";
}

function hasProcessAccess() {
    try {
        return typeof process !== "undefined";
    } catch (err) {
        return false;
    }
}

/**
 * Temporarily clear some environment variables that are used to associate the current connection with the emulator
 *
 * This avoids the emulator env vars taking effect during the inner function
 */
export function withoutEmulatorEnvVars<T>(inner: () => T): T {
    if (hasProcessAccess()) {
        const previousEnvVars = clone(process?.env || {});

        try {
            process.env = omit(process?.env || {}, ...emulatorEnvVars) as NodeJS.ProcessEnv;
            return inner();
        } finally {
            process.env = previousEnvVars;
        }
    } else {
        return inner();
    }
}
